import { TextFieldProps } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { TextField } from 'web-components-ts';

interface DurationInputProps extends Omit<TextFieldProps, 'name' | 'value' | 'onChange'> {
  name: string;
  value: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
}

const cleanDigits = (str: string): string => str.replace(/\D/g, '');

const formatDuration = (digits: string): string => {
  const clean = digits.replace(/\D/g, '').slice(-5).padStart(5, '0');
  const hours = clean.slice(0, clean.length - 2);
  const minutes = clean.slice(-2);
  return `${parseInt(hours, 10)}:${minutes}`;
};

const parseInitialValueToDigits = (formatted: string): string => {
  const parts = formatted.split(':');
  if (parts.length === 2) {
    return parts[0].padStart(3, '0') + parts[1].padStart(2, '0');
  }
  if (parts.length === 1) {
    return parts[0].padStart(2, '0');
  }
  return '';
};

/**
 * A duration input component that formats user input as a right-aligned duration string.
 *
 * Users can type digits continuously, and the component will automatically format them into:
 * - `mm` for minutes only (e.g., "05")
 * - `h:mm`, `hh:mm`, or `hhh:mm` as needed (e.g., "1:05", "12:45", "123:00")
 *
 * Supports:
 * - Initial value parsing from formats like "1:11"
 * - Real-time formatting while typing
 * - Deletion with Backspace
 * - Max input: 5 digits (e.g., "999:99")
 *
 * @param name - Field name used in the event target.
 * @param value - Current value of the input, expected in "H:MM" format.
 * @param onChange - Callback fired on every key press, returns `{ target: { name, value } }`.
 * @returns A controlled TextField rendering a duration input.
 */
export default function DurationInput({ name, value, onChange, ...restProps }: DurationInputProps) {
  const [buffer, setBuffer] = useState(() => cleanDigits(parseInitialValueToDigits(value)));

  useEffect(() => {
    if (!value) {
      setBuffer('');
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= '0' && e.key <= '9') {
      const updated = (buffer + e.key).slice(-5);
      setBuffer(updated);
      onChange({ target: { name, value: formatDuration(updated) } });
      e.preventDefault();
    }

    if (e.key === 'Backspace') {
      const updated = buffer.slice(0, -1);
      setBuffer(updated);
      onChange({ target: { name, value: formatDuration(updated) } });
      e.preventDefault();
    }
  };

  return (
    <TextField
      {...restProps}
      name={name}
      value={formatDuration(buffer)}
      onKeyDown={handleKeyDown}
      onChange={() => undefined}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*'
      }}
    />
  );
}
